"use client";
import {
  Box,
  Grid,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Image from "next/image";
import React, { useEffect, useMemo, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import WatchLaterIcon from "@mui/icons-material/WatchLater";
import { FormatedTime } from "../genericComponents/formated-time/FormatedTime";
import ViewDetails from "../genericComponents/view-details/ViewDetails";
import { AddToCartService } from "../../services/cart";
import { useCurrencyCode } from "../genericComponents/helper-functions/CurrencySymbol";
import { ViewDetailsServices } from "@/app/classes/main";

interface QuickCheckOutProps {
  QuickCheckoutData: any;
}

export const QuickCheckOut: React.FC<QuickCheckOutProps> = ({
  QuickCheckoutData,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("lg"));
  const [open, setOpen] = useState(false);
  const [dataList, setDataList] = useState<ViewDetailsServices[] | undefined>(
    undefined
  );
  const [data, setData] = useState<ViewDetailsServices | undefined>(undefined);
  const [reloadFlag, setReloadFlag] = useState(false);
  const addToCartService = useMemo(() => new AddToCartService(), []);
  const currencyCode = useCurrencyCode();
  const enrichedQuickCheckoutData = useMemo(
    () =>
      (QuickCheckoutData ?? []).map((service: any) => ({
        ...service,
        inCart: addToCartService.checkIfAvailableinCart(service.id, service.cid),
      })),
    [QuickCheckoutData, addToCartService]
  );

  const handleAddGift = async (e: any, index: number) => {
    e.stopPropagation();
    const selectedItemData = dataList && dataList[index];
    if (!selectedItemData) {
      return;
    }
    let inCart = addToCartService.checkIfAvailableinCart(
      selectedItemData?.id,
      selectedItemData?.cid
    );
    if (inCart) {
      await addToCartService.addToCart(
        QuickCheckoutData[index].id,
        false,
        QuickCheckoutData[index].cid,
        QuickCheckoutData[index]?.coverImage.secureHttpPath,
        QuickCheckoutData[index]?.aboutList,
        QuickCheckoutData[index]?.subHeading,
        QuickCheckoutData[index]?.serviceCharge,
        QuickCheckoutData[index]?.productCost,
        QuickCheckoutData[index]?.min,
        QuickCheckoutData[index]?.specialPrice,
        QuickCheckoutData[index]?.specialMessage
      );
    } else {
      await addToCartService.addToCart(
        QuickCheckoutData[index].id,
        true,
        QuickCheckoutData[index].cid,
        QuickCheckoutData[index]?.coverImage.secureHttpPath,
        QuickCheckoutData[index]?.aboutList,
        QuickCheckoutData[index]?.subHeading,
        QuickCheckoutData[index]?.serviceCharge,
        QuickCheckoutData[index]?.productCost,
        QuickCheckoutData[index]?.min,
        QuickCheckoutData[index]?.specialPrice,
        QuickCheckoutData[index]?.specialMessage
      );
    }
    var newDataList = dataList?.map((item: any, index: any) => {
      return {
        ...item,
        inCart: addToCartService.checkIfAvailableinCart(
          dataList[index].id,
          dataList[index].cid
        ),
      };
    });
    setDataList(newDataList);
    setReloadFlag(true);
  };

  useEffect(() => {
    setDataList(enrichedQuickCheckoutData);
  }, [enrichedQuickCheckoutData]);

  const handleOpenViewDetails = (item: any) => {
    setData(item);
    setOpen(true);
  };

  const railJustifyContent =
    (enrichedQuickCheckoutData?.length ?? 0) > 5 || isSmallScreen
      ? "flex-start"
      : "center";

  return (
    <>
      {dataList && dataList?.length > 0 ? (
        <Box
          sx={{
            marginTop: 0,
            width: "var(--salontym-content-width)",
            marginLeft: "auto",
            marginRight: "auto",
            marginBottom: "var(--salontym-section-gap)",
            background:
              "linear-gradient(135deg, rgba(16,33,23,0.98) 0%, rgba(26,55,38,0.96) 50%, rgba(91,45,31,0.92) 100%)",
            padding: { xs: "1rem 0 0.85rem", sm: "1.05rem 0 0.95rem" },
            borderRadius: { xs: "24px", sm: "28px" },
            boxShadow: "0 24px 56px rgba(11, 26, 19, 0.16)",
            overflow: "hidden",
            position: "relative",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: "-4rem",
              right: "-3rem",
              width: "14rem",
              height: "14rem",
              borderRadius: "999px",
              background:
                "radial-gradient(circle, rgba(255,255,255,0.14) 0%, rgba(255,255,255,0) 72%)",
            }}
          />
          <Grid item container xs={12} sm={12} md={12} lg={12} margin="auto">
            <Box
              sx={{
                textAlign: "center",
                flexDirection: "column",
                alignItems: "center",
                width: "100% ",
                position: "relative",
                zIndex: 1,
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  textAlign: "left",
                  color: "white",
                  display: "grid",
                  justifyContent: "flex-start",
                  alignItems: "start",
                  margin: { xs: "0.25rem 0 0.85rem", sm: "0.35rem 0 1rem" },
                  padding: { xs: "0 1rem", sm: "0 1.1rem" },
                  gap: "0.45rem",
                }}
              >
                <Box
                  className="section-eyebrow"
                  sx={{
                    background: "rgba(255,255,255,0.1)",
                    borderColor: "rgba(255,255,255,0.12)",
                    color: "#f7e7bf",
                    width: "fit-content",
                  }}
                >
                  Fast booking lane
                </Box>
                <Typography
                  sx={{
                    fontFamily: "var(--salontym-font-display)",
                    fontSize: { xs: "1.5rem", sm: "1.9rem" },
                    lineHeight: 1.04,
                    letterSpacing: "-0.03em",
                    color: "#fff8f1",
                    maxWidth: "16ch",
                  }}
                >
                  Quick Checkout
                </Typography>
                <Typography
                  sx={{
                    color: "rgba(255,248,241,0.72)",
                    fontSize: { xs: "0.9rem", sm: "0.95rem" },
                    lineHeight: 1.65,
                    maxWidth: "36rem",
                  }}
                >
                  Compact service cards for faster browsing and one-tap add to cart.
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: railJustifyContent,
                  overflowX: "auto",
                  scrollSnapType: "x proximity",
                  padding: { xs: "0 0.45rem", sm: "0 0.55rem" },
                  "&::-webkit-scrollbar": {
                    display: "none",
                  },
                  scrollbarWidth: "none", // Hide scrollbar for Firefox
                  msOverflowStyle: "none", // Hide scrollbar for IE/Edge
                }}
              >
                {dataList?.map((item: any, index: number) => (
                  <Box
                    key={item?.id ?? item?.cid ?? `quick-checkout-${index}`}
                    onClick={() => handleOpenViewDetails(item)}
                    sx={{
                      cursor: "pointer",
                      minWidth: { xs: "15.25rem", sm: "15.75rem" },
                      minHeight: { xs: "18rem", sm: "18.4rem" },
                      width: { xs: "15.25rem", sm: "15.75rem" },
                      height: { xs: "18rem", sm: "18.4rem" },
                      borderRadius: "1.1rem",
                      overflow: "hidden",
                      position: "relative",
                      margin: { xs: "0.4rem", sm: "0.6rem" },
                      boxShadow: "0 16px 32px rgba(11, 26, 19, 0.18)",
                      border: "1px solid rgba(255,255,255,0.14)",
                      background:
                        "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,251,249,0.97) 100%)",
                      scrollSnapAlign: "start",
                      transition: "transform 180ms ease, box-shadow 180ms ease",
                      "&:hover": {
                        transform: { xs: "none", sm: "translateY(-3px)" },
                        boxShadow: "0 20px 40px rgba(11, 26, 19, 0.22)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        padding: "0.7rem",
                      }}
                    >
                      <Box
                        sx={{
                          position: "relative",
                          height: { xs: "8rem", sm: "8.25rem" },
                          borderRadius: "0.9rem",
                          overflow: "hidden",
                          background:
                            "linear-gradient(135deg, rgba(223,246,232,0.9), rgba(255,247,235,0.92))",
                          flexShrink: 0,
                        }}
                      >
                        <Image
                          src={item?.coverImage?.secureHttpPath}
                          width={500}
                          height={500}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                          alt={item?.name ? `${item.name} service` : "Service image"}
                        />
                        <Box
                          sx={{
                            position: "absolute",
                            inset: 0,
                            background:
                              "linear-gradient(to top, rgba(16,33,23,0.5) 0%, rgba(16,33,23,0.08) 55%, rgba(16,33,23,0) 100%)",
                          }}
                        />
                      </Box>
                      <Box
                        sx={{
                          textAlign: "left",
                          padding: "0.7rem 0.1rem 0",
                          display: "flex",
                          flexDirection: "column",
                          minHeight: 0,
                          flex: 1,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#102117",
                            marginBottom: "0",
                            fontWeight: 700,
                            fontSize: { xs: "0.94rem", sm: "0.98rem" },
                            lineHeight: 1.24,
                          }}
                        >
                          {item?.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#5e6d63",
                            display: "block",
                            marginTop: "0.2rem",
                            lineHeight: 1.45,
                          }}
                        >
                          {item?.subHeading}
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "left",
                            alignItems: "center",
                            color: "#5e6d63",
                            marginTop: "0.45rem",
                          }}
                        >
                          <WatchLaterIcon sx={{ fontSize: "16px", color: "#1f7a4d" }} />
                          <Typography
                            variant="caption"
                            sx={{ paddingLeft: "8px", color: "#5e6d63" }}
                          >
                            <FormatedTime min={item?.min} />
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            maxHeight: "4.8rem",
                            overflow: "auto",
                            "&::-webkit-scrollbar": {
                              display: "none",
                            },
                            marginTop: "0.45rem",
                            paddingRight: "0.15rem",
                          }}
                        >
                          {item?.aboutList?.map(
                            (nestedItem: any, nestedIndex: number) => (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "left",
                                  alignItems: "start",
                                }}
                                key={`${item?.id ?? "service"}-detail-${nestedIndex}`}
                              >
                                <CheckIcon
                                  sx={{
                                    color: "#1f7a4d",
                                    paddingRight: "5px",
                                    fontSize: "0.95rem",
                                  }}
                                />
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "#44534a",
                                    fontSize: "0.72rem",
                                    lineHeight: 1.45,
                                  }}
                                >
                                  {nestedItem}
                                </Typography>
                              </Box>
                            )
                          )}
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "0.55rem 0.1rem 0.05rem",
                            marginTop: "auto",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontSize: "0.9rem", lineHeight: 1.2 }}
                          >
                            <span
                              style={{
                                color: "#15741a",
                                fontWeight: "bold",
                              }}
                            >
                              {currencyCode}
                              {item?.amount}/-
                            </span>{" "}
                            &nbsp;
                            <span
                              style={{
                                textDecoration: "line-through",
                                color: "#8a968f",
                              }}
                            >
                              {currencyCode}
                              {item?.mrp}/-
                            </span>
                          </Typography>
                          <Box>
                            {item.inCart == null || item.inCart === false ? (
                              <IconButton
                                onClick={(e) => handleAddGift(e, index)}
                                aria-label={`Add ${item?.name ?? "service"} to cart`}
                                sx={{
                                  height: "2rem",
                                  width: "2rem",
                                  color: "#15741a",
                                  background: "white",
                                  borderRadius: "10rem",
                                  border: "1px solid rgba(21,116,26,0.1)",
                                  "&:hover": {
                                    background: "rgba(223,246,232,0.8)",
                                  },
                                }}
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            ) : (
                              <IconButton
                                onClick={(e) => handleAddGift(e, index)}
                                aria-label={`Remove ${item?.name ?? "service"} from cart`}
                                sx={{
                                  height: "2rem",
                                  width: "2rem",
                                  color: "#15741a",
                                  background: "white",
                                  borderRadius: "10rem",
                                  border: "1px solid #15741a",
                                  "&:hover": {
                                    background: "rgba(223,246,232,0.8)",
                                  },
                                }}
                              >
                                <CheckIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Grid>
          <ViewDetails
            open={open}
            setClose={() => setOpen(false)}
            imageUrl={
              data?.banner?.secureHttpPath ?? data?.coverImage?.secureHttpPath
            }
            heading={data?.name}
            id={data?.id}
            cid={data?.cid}
            servicedDetails={data?.aboutList}
            descriptions={data?.descriptions}
            amount={data?.amount}
            min={data?.min}
            subHeading={data?.subHeading}
            mrp={data?.mrp}
            specialPrice={data?.specialPrice}
            specialMessage={data?.specialMessage}
            coin={data?.cashbackSalontymCoins}
            aboutList={data?.aboutList}
            serviceCharge={data?.serviceCharge}
            productCost={data?.productCost}
            reloadCartItemsFlag={() => {}}
            setReloadViewDetailsItem={() => setReloadFlag(false)}
            reloadViewDetailsItem={reloadFlag}
          />
        </Box>
      ) : (
        ""
      )}
    </>
  );
};
